import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useGetCruiseIqRelationships } from '~/hooks/apis/cruises/useCruiseIqRelationships';
import { useGetCruiseLines } from '~/hooks/apis/cruises/useGetCruiseLines';

import type {
  RelationshipDeparture,
  RelationshipDepartureRate,
  RelationshipOffer,
} from '~/services/cruises/CruiseIqService';

const COLORS = {
  offer: '#0d9488',
  departure: '#e11d48',
  rateCode: '#7c3aed',
};

const NODE_WIDTH = 8;
const MIN_NODE_HEIGHT = 32;
const NODE_GAP = 18;
const SVG_PADDING = { top: 50, bottom: 40, left: 180, right: 120 };
const LABEL_OFFSET = 14;
const COLUMN_POSITIONS = [0.18, 0.5, 0.82];
const SVG_WIDTH = 900;
const MIN_SVG_HEIGHT = 400;
const ROW_HEIGHT = MIN_NODE_HEIGHT + NODE_GAP;
const VISIBLE_ROWS = 6;
const SCROLL_HEIGHT = VISIBLE_ROWS * ROW_HEIGHT + SVG_PADDING.top + SVG_PADDING.bottom;
const MIN_STROKE = 1.5;
const MAX_STROKE = 10;
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.15;

type NodeType = 'offer' | 'departure' | 'rateCode';

type SelectedNode = {
  type: NodeType;
  id: string;
};

type SankeyNode = {
  id: string;
  type: NodeType;
  label: string;
  x: number;
  y: number;
  height: number;
};

type SankeyLink = {
  sourceId: string;
  targetId: string;
  sourceType: NodeType;
  targetType: NodeType;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  weight: number;
};

function computeSvgHeight(offerCount: number, departureCount: number, rateCodeCount: number): number {
  const maxNodes = Math.max(offerCount, departureCount, rateCodeCount);
  const needed = SVG_PADDING.top + SVG_PADDING.bottom + maxNodes * ROW_HEIGHT;
  return Math.max(needed, MIN_SVG_HEIGHT);
}

function computeStrokeWidth(weight: number, maxWeight: number): number {
  if (maxWeight <= 1) return MIN_STROKE;
  const normalized = weight / maxWeight;
  return MIN_STROKE + normalized * (MAX_STROKE - MIN_STROKE);
}

function buildSankeyLayout(
  offers: Array<RelationshipOffer>,
  departures: Array<RelationshipDeparture>,
  departureRates: Array<RelationshipDepartureRate>,
  svgWidth: number,
  svgHeight: number,
) {
  const nodes: Array<SankeyNode> = [];
  const links: Array<SankeyLink> = [];
  const nodeMap = new Map<string, SankeyNode>();

  const usableHeight = svgHeight - SVG_PADDING.top - SVG_PADDING.bottom;
  const usableWidth = svgWidth - SVG_PADDING.left - SVG_PADDING.right;

  const uniqueRateCodes = new Map<string, RelationshipDepartureRate>();
  for (const dr of departureRates) {
    if (!uniqueRateCodes.has(dr.rateCode)) {
      uniqueRateCodes.set(dr.rateCode, dr);
    }
  }
  const rateCodes = Array.from(uniqueRateCodes.values());

  const offerDepCounts = new Map<string, number>();
  for (const dep of departures) {
    offerDepCounts.set(dep.offerId, (offerDepCounts.get(dep.offerId) ?? 0) + 1);
  }

  const depRateCounts = new Map<string, number>();
  for (const dr of departureRates) {
    depRateCounts.set(dr.departureId, (depRateCounts.get(dr.departureId) ?? 0) + 1);
  }

  const maxOfferDeps = Math.max(...Array.from(offerDepCounts.values()), 1);
  const maxDepRates = Math.max(...Array.from(depRateCounts.values()), 1);

  const colX = COLUMN_POSITIONS.map((pct) => SVG_PADDING.left + pct * usableWidth);

  const layoutColumn = (items: Array<{ id: string; label: string }>, type: NodeType, colIdx: number) => {
    const count = items.length;
    if (count === 0) return;
    const nodeHeight = Math.min(Math.max((usableHeight - (count - 1) * NODE_GAP) / count, MIN_NODE_HEIGHT), 48);
    const actualTotal = count * nodeHeight + (count - 1) * NODE_GAP;
    const startY = SVG_PADDING.top + (usableHeight - actualTotal) / 2;

    items.forEach((item, i) => {
      const node: SankeyNode = {
        id: item.id,
        type,
        label: item.label,
        x: colX[colIdx],
        y: startY + i * (nodeHeight + NODE_GAP),
        height: nodeHeight,
      };
      nodes.push(node);
      nodeMap.set(`${type}-${item.id}`, node);
    });
  };

  layoutColumn(
    offers.map((o) => ({ id: o.id, label: o.name })),
    'offer',
    0,
  );

  layoutColumn(
    departures.map((d) => ({
      id: d.id,
      label: formatShortDate(d.departureDate),
    })),
    'departure',
    1,
  );

  layoutColumn(
    rateCodes.map((r) => ({
      id: r.rateCode,
      label: `${r.rateCode}\n${getRateLabel(r)}`,
    })),
    'rateCode',
    2,
  );

  for (const dep of departures) {
    const source = nodeMap.get(`offer-${dep.offerId}`);
    const target = nodeMap.get(`departure-${dep.id}`);
    if (source && target) {
      links.push({
        sourceId: dep.offerId,
        targetId: dep.id,
        sourceType: 'offer',
        targetType: 'departure',
        x1: source.x + NODE_WIDTH,
        y1: source.y + source.height / 2,
        x2: target.x,
        y2: target.y + target.height / 2,
        weight: offerDepCounts.get(dep.offerId) ?? 1,
      });
    }
  }

  for (const dr of departureRates) {
    const source = nodeMap.get(`departure-${dr.departureId}`);
    const target = nodeMap.get(`rateCode-${dr.rateCode}`);
    if (source && target) {
      links.push({
        sourceId: dr.departureId,
        targetId: dr.rateCode,
        sourceType: 'departure',
        targetType: 'rateCode',
        x1: source.x + NODE_WIDTH,
        y1: source.y + source.height / 2,
        x2: target.x,
        y2: target.y + target.height / 2,
        weight: depRateCounts.get(dr.departureId) ?? 1,
      });
    }
  }

  return { nodes, links, rateCodes, maxOfferDeps, maxDepRates };
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatFullDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function getRateLabel(rate: RelationshipDepartureRate): string {
  if (rate.isFlash) return 'Flash Rate';
  if (rate.isNetRate) return 'Net Rate';
  return 'Standard';
}

function bezierPath(x1: number, y1: number, x2: number, y2: number): string {
  const cx = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`;
}

type ConnectedSet = {
  offerIds: Set<string>;
  departureIds: Set<string>;
  rateCodeIds: Set<string>;
};

function buildConnectedSet(selected: SelectedNode | null, links: Array<SankeyLink>): ConnectedSet {
  const offerIds = new Set<string>();
  const departureIds = new Set<string>();
  const rateCodeIds = new Set<string>();

  if (!selected) return { offerIds, departureIds, rateCodeIds };

  if (selected.type === 'offer') {
    offerIds.add(selected.id);
    for (const link of links) {
      if (link.sourceType === 'offer' && link.sourceId === selected.id) {
        departureIds.add(link.targetId);
      }
    }
    for (const link of links) {
      if (link.sourceType === 'departure' && departureIds.has(link.sourceId)) {
        rateCodeIds.add(link.targetId);
      }
    }
  }

  if (selected.type === 'departure') {
    departureIds.add(selected.id);
    for (const link of links) {
      if (link.sourceType === 'offer' && link.targetId === selected.id) {
        offerIds.add(link.sourceId);
      }
      if (link.sourceType === 'departure' && link.sourceId === selected.id) {
        rateCodeIds.add(link.targetId);
      }
    }
  }

  if (selected.type === 'rateCode') {
    rateCodeIds.add(selected.id);
    for (const link of links) {
      if (link.targetType === 'rateCode' && link.targetId === selected.id) {
        departureIds.add(link.sourceId);
      }
    }
    for (const link of links) {
      if (link.sourceType === 'offer' && departureIds.has(link.targetId)) {
        offerIds.add(link.sourceId);
      }
    }
  }

  return { offerIds, departureIds, rateCodeIds };
}

function isLinkConnected(link: SankeyLink, connected: ConnectedSet): boolean {
  if (link.sourceType === 'offer') {
    return connected.offerIds.has(link.sourceId) && connected.departureIds.has(link.targetId);
  }
  if (link.sourceType === 'departure') {
    return connected.departureIds.has(link.sourceId) && connected.rateCodeIds.has(link.targetId);
  }
  return false;
}

function isNodeConnected(node: SankeyNode, connected: ConnectedSet): boolean {
  if (node.type === 'offer') return connected.offerIds.has(node.id);
  if (node.type === 'departure') return connected.departureIds.has(node.id);
  if (node.type === 'rateCode') return connected.rateCodeIds.has(node.id);
  return false;
}

function getNodeColor(type: NodeType): string {
  if (type === 'offer') return COLORS.offer;
  if (type === 'departure') return COLORS.departure;
  return COLORS.rateCode;
}

function getStatusColor(status: string): string {
  const s = status.toUpperCase();
  if (s === 'ACTIVE') return 'success.main';
  if (s === 'APPROVED') return 'info.main';
  if (s === 'DRAFT' || s === 'PENDING') return 'warning.main';
  if (s === 'CANCELLED' || s === 'INACTIVE' || s === 'DISABLED') return 'error.main';
  return 'text.secondary';
}

type DetailPanelProps = {
  selected: SelectedNode;
  offers: Array<RelationshipOffer>;
  departures: Array<RelationshipDeparture>;
  departureRates: Array<RelationshipDepartureRate>;
};

function DetailPanel({ selected, offers, departures, departureRates }: DetailPanelProps) {
  if (selected.type === 'departure') {
    const dep = departures.find((d) => d.id === selected.id);
    if (!dep) return null;
    const offer = offers.find((o) => o.id === dep.offerId);
    const connectedRates = departureRates.filter((dr) => dr.departureId === dep.id);
    const rateCodeNames = connectedRates.map((dr) => dr.rateCode);

    return (
      <Box>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Selected: Departure
        </Typography>
        <DetailRow label="DEPARTURE ID" value={dep.id} mono />
        <DetailRow label="DEPARTURE DATE" value={formatFullDate(dep.departureDate)} />
        <DetailRow label="OFFER" value={offer?.name ?? '-'} />
        <DetailRow label="OFFER ID" value={dep.offerId} mono />
        <DetailRow label="STATUS" value={dep.status} valueColor={getStatusColor(dep.status)} />
        <DetailRow label="CRUISE TYPE" value={dep.cruiseType ?? '-'} />
        <DetailRow label="CONNECTED RATE CODES" value={rateCodeNames.join(', ') || 'None'} />
        <DetailRow label="RATE CODE COUNT" value={String(connectedRates.length)} />
        <DetailRow label="BOOKINGS" value={String(dep.bookingCount)} />
      </Box>
    );
  }

  if (selected.type === 'offer') {
    const offer = offers.find((o) => o.id === selected.id);
    if (!offer) return null;
    const offerDepartures = departures.filter((d) => d.offerId === offer.id);
    const totalBookings = offerDepartures.reduce((sum, d) => sum + d.bookingCount, 0);
    const uniqueRates = new Set(
      departureRates.filter((dr) => offerDepartures.some((d) => d.id === dr.departureId)).map((dr) => dr.rateCode),
    );
    const dateRange =
      offerDepartures.length > 0
        ? `${formatShortDate(offerDepartures[0].departureDate)} – ${formatShortDate(
            offerDepartures[offerDepartures.length - 1].departureDate,
          )}`
        : '-';

    return (
      <Box>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Selected: Offer
        </Typography>
        <DetailRow label="OFFER ID" value={offer.id} mono />
        <DetailRow label="OFFER NAME" value={offer.name} />
        <DetailRow label="STATUS" value={offer.status} valueColor={getStatusColor(offer.status)} />
        <DetailRow label="CRUISE TYPE" value={offer.cruiseType ?? '-'} />
        <DetailRow label="DEPARTURES" value={String(offerDepartures.length)} />
        <DetailRow label="DEPARTURE RANGE" value={dateRange} />
        <DetailRow label="UNIQUE RATE CODES" value={String(uniqueRates.size)} />
        <DetailRow label="TOTAL BOOKINGS" value={String(totalBookings)} />
      </Box>
    );
  }

  if (selected.type === 'rateCode') {
    const rate = departureRates.find((dr) => dr.rateCode === selected.id);
    if (!rate) return null;
    const connectedDeps = departureRates.filter((dr) => dr.rateCode === selected.id).map((dr) => dr.departureId);
    const uniqueDeps = [...new Set(connectedDeps)];
    const connectedOfferIds = new Set(departures.filter((d) => uniqueDeps.includes(d.id)).map((d) => d.offerId));

    return (
      <Box>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Selected: Rate Code
        </Typography>
        <DetailRow label="RATE ID" value={rate.rateId} mono />
        <DetailRow label="RATE CODE" value={rate.rateCode} mono />
        <DetailRow label="TYPE" value={getRateLabel(rate)} />
        <DetailRow label="FLASH" value={rate.isFlash ? 'Yes' : 'No'} />
        <DetailRow label="NET RATE" value={rate.isNetRate ? 'Yes' : 'No'} />
        <DetailRow label="ENABLED" value={rate.isEnabled ? 'Yes' : 'No'} />
        <DetailRow label="CONNECTED DEPARTURES" value={String(uniqueDeps.length)} />
        <DetailRow label="CONNECTED OFFERS" value={String(connectedOfferIds.size)} />
      </Box>
    );
  }

  return null;
}

type DetailRowProps = {
  label: string;
  value: string;
  mono?: boolean;
  valueColor?: string;
};

function DetailRow({ label, value, mono, valueColor }: DetailRowProps) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '0.05em', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontFamily: mono ? 'monospace' : 'inherit',
          color: valueColor ?? 'text.primary',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SankeyDiagram({
  offers,
  departures,
  departureRates,
  selected,
  onSelect,
}: {
  offers: Array<RelationshipOffer>;
  departures: Array<RelationshipDeparture>;
  departureRates: Array<RelationshipDepartureRate>;
  selected: SelectedNode | null;
  onSelect: (node: SelectedNode | null) => void;
}) {
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const didDrag = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  const handleZoomIn = useCallback(() => {
    setZoom((z) => Math.min(z + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((z) => Math.max(z - ZOOM_STEP, MIN_ZOOM));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoom(1);
    if (containerRef.current) {
      containerRef.current.scrollLeft = 0;
      containerRef.current.scrollTop = 0;
    }
  }, []);

  const handleFullscreenToggle = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        setZoom((z) => {
          const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
          return Math.min(Math.max(z + delta, MIN_ZOOM), MAX_ZOOM);
        });
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const updateHeight = () => setContainerHeight(el.clientHeight);
    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);

    return () => observer.disconnect();
  }, [isFullscreen]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const container = containerRef.current;
    if (!container) return;
    isDragging.current = true;
    didDrag.current = false;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    };
    container.style.cursor = 'grabbing';
    container.style.userSelect = 'none';
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging.current || !containerRef.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      didDrag.current = true;
    }
    containerRef.current.scrollLeft = dragStart.current.scrollLeft - dx;
    containerRef.current.scrollTop = dragStart.current.scrollTop - dy;
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
      containerRef.current.style.removeProperty('user-select');
    }
  }, []);

  const uniqueRateCount = useMemo(() => {
    const codes = new Set(departureRates.map((dr) => dr.rateCode));
    return codes.size;
  }, [departureRates]);

  const svgHeight = useMemo(
    () => computeSvgHeight(offers.length, departures.length, uniqueRateCount),
    [offers.length, departures.length, uniqueRateCount],
  );

  const { nodes, links, maxOfferDeps, maxDepRates } = useMemo(
    () => buildSankeyLayout(offers, departures, departureRates, SVG_WIDTH, svgHeight),
    [offers, departures, departureRates, svgHeight],
  );

  const colX = COLUMN_POSITIONS.map(
    (pct) => SVG_PADDING.left + pct * (SVG_WIDTH - SVG_PADDING.left - SVG_PADDING.right),
  );

  const connectedSet = useMemo(() => buildConnectedSet(selected, links), [selected, links]);

  const columnHeaders = ['OFFERS', 'DEPARTURES', 'RATE CODES'];
  const zoomPercent = Math.round(zoom * 100);
  const scaledSvgHeight = svgHeight * zoom;
  const shouldCenterFullscreenVertically = isFullscreen && scaledSvgHeight < containerHeight;

  return (
    <Box
      ref={wrapperRef}
      sx={{
        bgcolor: isFullscreen ? 'background.paper' : 'transparent',
        p: isFullscreen ? 2 : 0,
        display: isFullscreen ? 'flex' : 'block',
        flexDirection: 'column',
        height: isFullscreen ? '100vh' : 'auto',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexShrink: 0 }}>
        {!isFullscreen && (
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
            Offers &rarr; Departures &rarr; Rate Codes
          </Typography>
        )}
        {isFullscreen && <Box />}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Zoom out">
            <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= MIN_ZOOM}>
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{ minWidth: 36, textAlign: 'center', fontFamily: 'monospace', color: 'text.secondary' }}
          >
            {zoomPercent}%
          </Typography>
          <Tooltip title="Zoom in">
            <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= MAX_ZOOM}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset zoom">
            <IconButton size="small" onClick={handleZoomReset}>
              <CenterFocusStrongIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={handleFullscreenToggle}>
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Stack direction="row" sx={{ flex: isFullscreen ? 1 : 'none', minHeight: 0 }}>
        <Box
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          sx={{
            flex: 1,
            maxHeight: isFullscreen ? 'calc(100vh - 100px)' : SCROLL_HEIGHT,
            overflow: 'auto',
            cursor: 'grab',
          }}
        >
          <Box
            onClick={() => {
              if (!didDrag.current) onSelect(null);
            }}
            sx={{
              minHeight: isFullscreen ? '100%' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: shouldCenterFullscreenVertically ? 'center' : 'flex-start',
            }}
          >
            <svg
              width={SVG_WIDTH * zoom}
              height={scaledSvgHeight}
              viewBox={`0 0 ${SVG_WIDTH} ${svgHeight}`}
              style={{ display: 'block' }}
              onClick={() => {
                if (!didDrag.current) onSelect(null);
              }}
            >
              <rect width={SVG_WIDTH} height={svgHeight} fill="transparent" />
              {columnHeaders.map((header, i) => (
                <text
                  key={header}
                  x={colX[i] + NODE_WIDTH / 2}
                  y={24}
                  textAnchor="middle"
                  fontSize={10}
                  fontWeight={700}
                  letterSpacing="0.1em"
                  fill="#94a3b8"
                >
                  {header}
                </text>
              ))}

              {links.map((link, i) => {
                const highlighted = isLinkConnected(link, connectedSet);
                const hasSelection = selected !== null;
                const isOfferLink = link.sourceType === 'offer';
                const maxWeight = isOfferLink ? maxOfferDeps : maxDepRates;
                const strokeWidth = highlighted
                  ? computeStrokeWidth(link.weight, maxWeight) + 1
                  : computeStrokeWidth(link.weight, maxWeight);

                return (
                  <path
                    key={i}
                    d={bezierPath(link.x1, link.y1, link.x2, link.y2)}
                    fill="none"
                    stroke={isOfferLink ? COLORS.offer : COLORS.departure}
                    strokeWidth={strokeWidth}
                    opacity={hasSelection ? (highlighted ? 0.85 : 0.08) : 0.35}
                  />
                );
              })}

              {nodes.map((node) => {
                const connected = isNodeConnected(node, connectedSet);
                const hasSelection = selected !== null;
                const isSelected = selected?.type === node.type && selected?.id === node.id;
                const color = getNodeColor(node.type);

                return (
                  <g
                    key={`${node.type}-${node.id}`}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!didDrag.current) onSelect({ type: node.type, id: node.id });
                    }}
                  >
                    <rect
                      x={node.x}
                      y={node.y}
                      width={NODE_WIDTH}
                      height={node.height}
                      rx={3}
                      fill={color}
                      opacity={hasSelection ? (connected ? 1 : 0.15) : 1}
                      stroke={isSelected ? '#1e293b' : 'none'}
                      strokeWidth={isSelected ? 2 : 0}
                    />
                    {node.type === 'offer' && (
                      <>
                        <text
                          x={node.x - LABEL_OFFSET}
                          y={node.y + node.height / 2 - 6}
                          textAnchor="end"
                          dominantBaseline="central"
                          fontSize={11}
                          fill={hasSelection && !connected ? '#cbd5e1' : '#334155'}
                          fontWeight={isSelected ? 700 : 400}
                        >
                          {truncate(node.label, 30)}
                        </text>
                        <text
                          x={node.x - LABEL_OFFSET}
                          y={node.y + node.height / 2 + 7}
                          textAnchor="end"
                          dominantBaseline="central"
                          fontSize={8}
                          fill={hasSelection && !connected ? '#e2e8f0' : '#94a3b8'}
                          fontFamily="monospace"
                        >
                          {node.id.slice(0, 8)}
                        </text>
                      </>
                    )}
                    {node.type === 'departure' && (
                      <>
                        <text
                          x={node.x + NODE_WIDTH + LABEL_OFFSET}
                          y={node.y + node.height / 2 - 6}
                          textAnchor="start"
                          dominantBaseline="central"
                          fontSize={11}
                          fill={hasSelection && !connected ? '#cbd5e1' : '#334155'}
                          fontWeight={isSelected ? 700 : 400}
                        >
                          {node.label}
                        </text>
                        <text
                          x={node.x + NODE_WIDTH + LABEL_OFFSET}
                          y={node.y + node.height / 2 + 7}
                          textAnchor="start"
                          dominantBaseline="central"
                          fontSize={8}
                          fill={hasSelection && !connected ? '#e2e8f0' : '#94a3b8'}
                          fontFamily="monospace"
                        >
                          {node.id.slice(0, 8)}
                        </text>
                      </>
                    )}
                    {node.type === 'rateCode' && (
                      <>
                        <text
                          x={node.x + NODE_WIDTH + LABEL_OFFSET}
                          y={node.y + node.height / 2 - 7}
                          textAnchor="start"
                          dominantBaseline="central"
                          fontSize={11}
                          fill={hasSelection && !connected ? '#cbd5e1' : '#334155'}
                          fontWeight={isSelected ? 700 : 500}
                        >
                          {node.label.split('\n')[0]}
                        </text>
                        <text
                          x={node.x + NODE_WIDTH + LABEL_OFFSET}
                          y={node.y + node.height / 2 + 7}
                          textAnchor="start"
                          dominantBaseline="central"
                          fontSize={9}
                          fill={hasSelection && !connected ? '#e2e8f0' : '#64748b'}
                        >
                          {node.label.split('\n')[1] ?? ''}
                        </text>
                      </>
                    )}
                  </g>
                );
              })}
            </svg>
          </Box>
        </Box>

        {isFullscreen && selected && (
          <Paper
            elevation={0}
            sx={{
              width: 300,
              flexShrink: 0,
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              ml: 2,
              overflowY: 'auto',
              alignSelf: 'center',
              maxHeight: isFullscreen ? 'calc(100vh - 100px)' : 'auto',
            }}
          >
            <DetailPanel selected={selected} offers={offers} departures={departures} departureRates={departureRates} />
          </Paper>
        )}
      </Stack>

      <Stack direction="row" spacing={3} sx={{ mt: 1.5, ml: 2, flexShrink: 0 }}>
        <LegendItem color={COLORS.offer} label="Offers" />
        <LegendItem color={COLORS.departure} label="Departures" />
        <LegendItem color={COLORS.rateCode} label="Rate Codes" />
      </Stack>
    </Box>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box sx={{ width: 12, height: 12, bgcolor: color, borderRadius: '2px' }} />
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

function truncate(str: string, max: number): string {
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
}

type VendorOption = { id: string; label: string };
type OfferOption = { id: string; name: string };

const MAX_SELECTED_OFFERS = 5;

type FilterBarProps = {
  vendorOptions: Array<VendorOption>;
  loadingVendors: boolean;
  selectedVendor: VendorOption | null;
  onVendorChange: (_: unknown, value: VendorOption | null) => void;
  offerOptions: Array<OfferOption>;
  loadingOffers: boolean;
  selectedOffers: Array<OfferOption>;
  onOfferChange: (_: unknown, value: Array<OfferOption>) => void;
};

const FilterBar = React.memo(function FilterBar({
  vendorOptions,
  loadingVendors,
  selectedVendor,
  onVendorChange,
  offerOptions,
  loadingOffers,
  selectedOffers,
  onOfferChange,
}: FilterBarProps) {
  const filterOptions = useCallback((options: Array<OfferOption>, state: { inputValue: string }) => {
    const term = state.inputValue.toLowerCase();
    const filtered = term
      ? options.filter((o) => o.name.toLowerCase().includes(term) || o.id.toLowerCase().includes(term))
      : options;
    return filtered.slice(0, 100);
  }, []);

  const filterVendorOptions = useCallback((options: Array<VendorOption>, state: { inputValue: string }) => {
    const term = state.inputValue.toLowerCase().trim();
    if (!term) return options;
    return options.filter((option) => option.label.toLowerCase().includes(term));
  }, []);

  return (
    <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" fontWeight={600} color="text.secondary">
          Vendor:
        </Typography>
        <Autocomplete
          size="small"
          options={vendorOptions}
          value={selectedVendor}
          onChange={onVendorChange}
          loading={loadingVendors}
          getOptionLabel={(option) => option.label}
          filterOptions={filterVendorOptions}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          sx={{ minWidth: 240 }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select a vendor..." InputLabelProps={{ shrink: true }} />
          )}
        />
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" fontWeight={600} color="text.secondary">
          Offer:
        </Typography>
        <Autocomplete
          multiple
          size="small"
          options={offerOptions}
          value={selectedOffers}
          onChange={onOfferChange}
          loading={loadingOffers}
          disabled={!selectedVendor}
          limitTags={2}
          disableCloseOnSelect
          getOptionLabel={(option) => option.name}
          getOptionDisabled={() => selectedOffers.length >= MAX_SELECTED_OFFERS}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          filterOptions={filterOptions}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1, fontFamily: 'monospace' }}
              >
                {option.id.slice(0, 8)}
              </Typography>
            </li>
          )}
          noOptionsText="No offers found"
          sx={{ minWidth: 360 }}
          ListboxProps={{ style: { maxHeight: 300 } }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={selectedOffers.length === 0 ? 'Search by name or ID...' : ''}
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
      </Stack>
    </Stack>
  );
});

export default function RelationshipExplorer() {
  const [selectedVendor, setSelectedVendor] = useState<VendorOption | null>(null);
  const [selectedOffers, setSelectedOffers] = useState<Array<OfferOption>>([]);
  const [selectedNode, setSelectedNode] = useState<SelectedNode | null>(null);

  const { data: cruiseLines = [], isLoading: loadingVendors } = useGetCruiseLines();

  const vendorOptions: Array<VendorOption> = useMemo(() => {
    const seen = new Set<string>();
    return cruiseLines
      .map((cl) => ({ id: String(cl.id), label: cl.name }))
      .filter((option) => {
        const key = option.label.trim().toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [cruiseLines]);

  const hasOfferSelection = selectedOffers.length > 0;
  const effectiveOfferIds = hasOfferSelection ? selectedOffers.map((o) => o.id) : undefined;

  const { data: allVendorData, isFetching: fetchingAllOffers } = useGetCruiseIqRelationships(selectedVendor?.id ?? '');

  const {
    data: relationshipData,
    isFetching: fetchingRelationships,
    error: relationshipError,
  } = useGetCruiseIqRelationships(hasOfferSelection ? selectedVendor?.id ?? '' : '', effectiveOfferIds);

  const deferredData = useDeferredValue(relationshipData);
  const isRenderingStale = deferredData !== relationshipData;
  const loadingRelationships = (fetchingRelationships || isRenderingStale) && !deferredData;

  const vendorOffers = useMemo(() => allVendorData?.offers ?? [], [allVendorData]);

  const handleVendorChange = useCallback((_: unknown, value: VendorOption | null) => {
    setSelectedVendor(value);
    setSelectedOffers([]);
    setSelectedNode(null);
  }, []);

  const handleOfferChange = useCallback((_: unknown, value: Array<OfferOption>) => {
    if (value.length <= MAX_SELECTED_OFFERS) {
      setSelectedOffers(value);
      setSelectedNode(null);
    }
  }, []);

  const handleNodeSelect = useCallback((node: SelectedNode | null) => {
    if (!node) {
      setSelectedNode(null);
      return;
    }
    setSelectedNode((prev) => (prev?.type === node.type && prev?.id === node.id ? null : node));
  }, []);

  const hasData = deferredData && (deferredData.offers.length > 0 || deferredData.departures.length > 0);
  const showOverlay = fetchingRelationships || isRenderingStale;

  return (
    <Box>
      <FilterBar
        vendorOptions={vendorOptions}
        loadingVendors={loadingVendors}
        selectedVendor={selectedVendor}
        onVendorChange={handleVendorChange}
        offerOptions={vendorOffers}
        loadingOffers={fetchingAllOffers}
        selectedOffers={selectedOffers}
        onOfferChange={handleOfferChange}
      />

      {!selectedVendor && (
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'grey.300',
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
            Select a vendor to get started
          </Typography>
          <Typography variant="body2" color="text.disabled" maxWidth={420} mx="auto">
            Choose a cruise line from the dropdown above, then pick up to 5 offers to visualize how they connect to
            departures and rate codes.
          </Typography>
        </Paper>
      )}

      {selectedVendor && fetchingAllOffers && !hasOfferSelection && (
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Loading offers for {selectedVendor.label}...
          </Typography>
        </Box>
      )}

      {selectedVendor && !fetchingAllOffers && !hasOfferSelection && vendorOffers.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'grey.300',
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
            No offers found
          </Typography>
          <Typography variant="body2" color="text.disabled" maxWidth={420} mx="auto">
            {selectedVendor.label} has no offers in the system. Try a different vendor.
          </Typography>
        </Paper>
      )}

      {selectedVendor && !fetchingAllOffers && !hasOfferSelection && vendorOffers.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'grey.300',
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
            Select up to 5 offers
          </Typography>
          <Typography variant="body2" color="text.disabled" maxWidth={420} mx="auto">
            {vendorOffers.length} offers available for {selectedVendor.label}. Pick the ones you want to explore from
            the dropdown above.
          </Typography>
        </Paper>
      )}

      {selectedVendor && hasOfferSelection && loadingRelationships && (
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      )}

      {relationshipError && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="error" variant="outlined">
            {(relationshipError as Error).message || 'Failed to load relationship data'}
          </Alert>
        </Box>
      )}

      {selectedVendor && hasOfferSelection && !loadingRelationships && hasData && deferredData && (
        <Stack direction="row" spacing={2} sx={{ transition: 'all 0.3s ease', position: 'relative' }}>
          {showOverlay && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.6)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 3,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              minWidth: 0,
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              overflow: 'hidden',
              transition: 'flex 0.3s ease',
            }}
          >
            <SankeyDiagram
              offers={deferredData.offers}
              departures={deferredData.departures}
              departureRates={deferredData.departureRates}
              selected={selectedNode}
              onSelect={handleNodeSelect}
            />
          </Paper>

          {selectedNode && (
            <Paper
              elevation={0}
              sx={{
                width: 300,
                flexShrink: 0,
                p: 3,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                alignSelf: 'flex-start',
                position: 'sticky',
                top: 16,
              }}
            >
              <DetailPanel
                selected={selectedNode}
                offers={deferredData.offers}
                departures={deferredData.departures}
                departureRates={deferredData.departureRates}
              />
            </Paper>
          )}
        </Stack>
      )}

      {selectedVendor && hasOfferSelection && !loadingRelationships && !hasData && !relationshipError && (
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
            No relationship data found
          </Typography>
          <Typography variant="body2" color="text.disabled">
            The selected offers have no departures or rate codes to display.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
